// src/pages/PrivacyPolicy.jsx
import React, { useEffect, useContext } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

import Header from './Header';
import Footer from './Footer';
import { Helmet } from 'react-helmet';
import { AppContext } from './AppContext';


export default function PrivacyPolicy() {
    const { settings } = useContext(AppContext);
    const {
        gmail_link,
        phone_link,
    } = settings || {};


    useEffect(() => {
        AOS.init({ once: true });
    }, []);

    return (
        <>
            <Helmet>
                <title>Privacy Policy | PropXplorer</title>
                <meta
                    name="description"
                    content="Privacy Policy of PropXplorer"
                />
            </Helmet>

            <Header />

            <main className="font-[Rubik] p-6 lg:p-16 max-w-4xl mx-auto" data-aos="fade-up">
                <article>
                    <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
                    <p className="italic mb-8">Effective Date: 27 March 2025</p>

                    <h2 className="text-2xl font-semibold mt-8 mb-2">Introduction</h2>
                    <p className="text-base text-justify leading-relaxed mb-6">
                        At PropXplorer (“we,” “our,” “us”), we value your privacy and are committed to protecting your personal information. This Privacy Policy outlines how we collect, use, and safeguard your data when you use our services.
                    </p>

                    <h2 className="text-2xl font-semibold mt-8 mb-2">Information We Collect</h2>
                    <p className="text-base text-justify leading-relaxed mb-4">
                        We may collect information directly from you or through third-party platforms, including but not limited to META, Google, 99 Acres, MagicBricks, Housing.com, and similar sources. The information we collect includes:
                    </p>
                    <ul className="list-disc pl-6 mb-6 text-base text-justify leading-relaxed">
                        <li>
                            <strong>Personal Information:</strong> Your name, email address, phone number, and property preferences.
                        </li>
                        <li>
                            <strong>Usage Data:</strong> Details such as your IP address, browser type, and pages you visit.
                        </li>
                        <li>
                            <strong>Communication Data:</strong> Messages and preferences related to platforms like SMS, WhatsApp, RCS, and others.
                        </li>
                    </ul>

                    <h2 className="text-2xl font-semibold mt-8 mb-2">How We Use Your Information</h2>
                    <p className="text-base text-justify leading-relaxed mb-4">We use your information to:</p>
                    <ul className="list-disc pl-6 mb-6 text-base text-justify leading-relaxed">
                        <li>Provide property-related services, updates, and personalized recommendations.</li>
                        <li>Enhance website performance and improve your overall user experience.</li>
                        <li>Send promotional messages, only with your explicit consent.</li>
                        <li>Deliver targeted advertisements through platforms like Google Ads and Meta.</li>
                    </ul>

                    <h2 className="text-2xl font-semibold mt-8 mb-2">Sharing and Disclosure</h2>
                    <p className="text-base text-justify leading-relaxed mb-6">
                        We <strong>do not sell or share</strong> your personal data with third parties for marketing purposes. However, we may use collected data (in an anonymized or aggregated form) for targeted advertising on platforms like Google Ads and Meta.
                    </p>

                    <h2 className="text-2xl font-semibold mt-8 mb-2">Consent and Opt-Out</h2>
                    <p className="text-base text-justify leading-relaxed mb-6">
                        By providing your details, you consent to receive communications from PropXplorer via SMS, WhatsApp, RCS, and other platforms. You can withdraw your consent at any time by contacting us or using the opt-out link in our messages.
                    </p>

                    <h2 className="text-2xl font-semibold mt-8 mb-2">Data Protection</h2>
                    <p className="text-base text-justify leading-relaxed mb-6">
                        We implement robust security measures to protect your information from unauthorized access, alteration, and misuse. Your data privacy and security are our top priorities.
                    </p>

                    <h2 className="text-2xl font-semibold mt-8 mb-2">Cookies and Tracking</h2>
                    <p className="text-base text-justify leading-relaxed mb-6">
                        We use cookies to enhance your browsing experience and gather usage data for analytical purposes. You can manage your cookie preferences through your browser settings.
                    </p>

                    <h2 className="text-2xl font-semibold mt-8 mb-2">Changes to This Policy</h2>
                    <p className="text-base text-justify leading-relaxed mb-6">
                        PropXplorer may update this privacy policy periodically. Any changes will be posted on this page with the revised effective date. Please check this page regularly to stay informed about how we protect your data.
                    </p>

                    <h2 className="text-2xl font-semibold mt-8 mb-2">Contact Us</h2>
                    <p className="text-base text-justify leading-relaxed">
                        If you have any questions or concerns about this Privacy Policy, contact us at:
                    </p>
                    <p className="text-base text-justify leading-relaxed mb-6">
                        <strong>Email:</strong>{' '}
                        <a
                            href={`mailto:${gmail_link}`}
                            className="text-blue-400 hover:underline"
                        >
                            {gmail_link}
                        </a>
                        <br />
                        <strong>Phone:</strong>{' '}
                        <a
                            href={`tel:+${phone_link}`}
                            className="text-blue-400 hover:underline"
                        >
                            +{phone_link}
                        </a>
                    </p>

                </article>
            </main>

            <Footer />
        </>
    );
}
